import React, { useEffect, useState, useCallback, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Swal from "sweetalert2";
import {
  AiOutlinePlus, AiOutlineEdit, AiOutlineDelete, AiOutlineEye, AiOutlineSearch,
} from "react-icons/ai";
import { IoIosPeople } from "react-icons/io";
import { MdLockReset } from "react-icons/md";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useSchoolRole } from "../../hooks/useSchoolRole";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const PER_PAGE = 8;

const genPassword = () => {
  const b = new Uint8Array(9);
  window.crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/[+/=]/g, "").slice(0, 12);
};

const MONO = [
  ["#eb2178", "#ff7bb0"], ["#632d43", "#a24d6f"], ["#2e7d6b", "#5bc0aa"],
  ["#3b5bdb", "#7d97ff"], ["#d9770a", "#ffb64d"], ["#7048e8", "#a487ff"],
];
const initials = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  return ((w[0][0] || "") + (w[1]?.[0] || w[0][1] || "")).toUpperCase();
};
const hashIdx = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % MONO.length;
};
const Monogram = ({ name }) => {
  const [a, b] = MONO[hashIdx(name || "")];
  return (
    <span className="sr-mono-av" style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}>
      {initials(name)}
    </span>
  );
};

const Pager = ({ t, page, total, perPage, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  return (
    <div className="sr-pager">
      <button type="button" className="sr-page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      <span className="sr-page-info">{t("pageWord")} {page} / {totalPages}</span>
      <button type="button" className="sr-page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
    </div>
  );
};

/** Selector de alumnos: buscador + lista con checkboxes. */
const StudentPicker = ({ t, students, selected, onToggle }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return students;
    return students.filter((x) =>
      String(x.learnerName || "").toLowerCase().includes(s) ||
      String(x.email || "").toLowerCase().includes(s) ||
      String(x.section || "").toLowerCase().includes(s)
    );
  }, [q, students]);
  return (
    <div>
      <div className="sr-label">{t("assignStudents")} <span className="sr-count">{selected.length}</span></div>
      <div className="sr-search">
        <AiOutlineSearch />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchStudent")} />
      </div>
      <div className="sr-picker">
        {filtered.length === 0 ? (
          <div className="sr-hint px-2 py-2">{t("noStudents")}</div>
        ) : filtered.map((s) => (
          <label key={s.uid} className={`sr-pick-row ${selected.includes(s.uid) ? "on" : ""}`}>
            <input type="checkbox" checked={selected.includes(s.uid)} onChange={() => onToggle(s.uid)} />
            <Monogram name={s.learnerName} />
            <span className="sr-pick-info">
              <span className="sr-person-name">{s.learnerName || "—"}</span>
              <span className="sr-sub">{[s.grade, s.section].filter(Boolean).join(" · ") || s.email}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

const SchoolRepresentatives = () => {
  const { t } = useTranslations();
  const { role, loading: roleLoading } = useSchoolRole();
  const isSchool = role === "escuela";

  const [reps, setReps] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Formulario crear
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState(genPassword());
  const [sel, setSel] = useState([]);
  const [saving, setSaving] = useState(false);

  const headers = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = await headers();
      const [rr, ur] = await Promise.all([
        fetch(`${API_BASE}/api/school/representatives`, { headers: h }).then((r) => r.json()),
        fetch(`${API_BASE}/api/school/students`, { headers: h }).then((r) => r.json()),
      ]);
      setReps(rr.representatives || []);
      setStudents(ur.students || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (!roleLoading && isSchool) load();
  }, [roleLoading, isSchool, load]);

  const err = (e) => Swal.fire({ icon: "error", title: t("errorTitle"), text: e.message });
  const toggle = (uid) => setSel((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));

  const createRep = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/representatives`, {
        method: "POST", headers: h,
        body: JSON.stringify({ email: email.trim(), password: pass, displayName: name.trim(), studentUids: sel }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({
        icon: "success", title: t("representativeCreated"),
        html: `<div style="text-align:left"><b>${name || email}</b><br/>${email}<br/>${t("adminPasswordLabel")}: <code>${data.password || pass}</code></div>`,
      });
      setName(""); setEmail(""); setPass(genPassword()); setSel([]);
      load();
    } catch (e2) { err(e2); } finally { setSaving(false); }
  };

  const resetRep = async (r) => {
    const c = await Swal.fire({
      title: t("resetPasswordConfirm"), html: `<b>${r.displayName || r.email}</b><br/>${t("resetPasswordConfirmText")}`,
      icon: "question", showCancelButton: true, confirmButtonText: t("resetPassword"), cancelButtonText: t("cancel"), confirmButtonColor: "#eb2178",
    });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/representatives`, { method: "PATCH", headers: h, body: JSON.stringify({ uid: r.uid, resetPassword: true }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({ icon: "success", title: t("newPasswordGenerated"), html: `${r.email}<br/><code>${data.password}</code>` });
    } catch (e2) { err(e2); }
  };

  const deleteRep = async (r) => {
    const c = await Swal.fire({ title: t("deleteRepresentativeConfirm"), text: r.email, icon: "warning", showCancelButton: true });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/representatives?uid=${r.uid}`, { method: "DELETE", headers: h });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      load();
    } catch (e2) { err(e2); }
  };

  // Editar
  const [editRep, setEditRep] = useState(null);
  const [eName, setEName] = useState("");
  const [eSel, setESel] = useState([]);
  const [savingE, setSavingE] = useState(false);
  const openEdit = (r) => {
    setEditRep(r);
    setEName(r.displayName || "");
    setESel((r.students || []).map((s) => s.uid));
  };
  const toggleE = (uid) => setESel((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      setSavingE(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/representatives`, {
        method: "PATCH", headers: h,
        body: JSON.stringify({ uid: editRep.uid, displayName: eName.trim(), studentUids: eSel }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      setEditRep(null); load();
    } catch (e2) { err(e2); } finally { setSavingE(false); }
  };

  const [detail, setDetail] = useState(null);

  const totalPages = Math.max(1, Math.ceil(reps.length / PER_PAGE));
  const p = Math.min(page, totalPages);
  const repsPage = reps.slice((p - 1) * PER_PAGE, p * PER_PAGE);

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto sr-wrap">
          <div className="sr-head">
            <div className="sr-head-ic"><IoIosPeople /></div>
            <div>
              <div className="sr-eyebrow">{t("schoolPanelTitle")}</div>
              <h2 className="sr-title">{t("representatives")}</h2>
            </div>
          </div>
          <p className="sr-desc">{t("representativesDesc")}</p>

          {roleLoading || loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: "var(--sr-plum)" }} /></div>
          ) : !isSchool ? (
            <div className="sr-card"><div className="sr-empty"><div className="sr-empty-ic">🔒</div><div className="sr-empty-t">{t("notAuthorized")}</div></div></div>
          ) : (
            <div className="sr-card">
              <div className="sr-card-head">
                <h5 className="sr-card-title">{t("representatives")} <span className="sr-count">{reps.length}</span></h5>
              </div>

              {students.length === 0 ? (
                <div className="sr-empty"><div className="sr-empty-ic">🎒</div><div className="sr-empty-t">{t("noStudentsYet")}</div><div className="sr-empty-h">{t("representativesNeedStudents")}</div></div>
              ) : (
                <Form onSubmit={createRep} className="sr-form">
                  <Row className="g-2">
                    <Col md={5} xs={12}><Form.Control className="sr-input" placeholder={t("representativeName")} value={name} onChange={(e) => setName(e.target.value)} /></Col>
                    <Col md={7} xs={12}><Form.Control className="sr-input" type="email" placeholder={t("representativeEmail")} value={email} onChange={(e) => setEmail(e.target.value)} required /></Col>
                    <Col md={9} xs={8}><Form.Control className="sr-input" value={pass} onChange={(e) => setPass(e.target.value)} required /></Col>
                    <Col md={3} xs={4}><button type="button" className="sr-btn sr-btn-soft w-100" onClick={() => setPass(genPassword())}>{t("generate")}</button></Col>
                    <Col xs={12}><StudentPicker t={t} students={students} selected={sel} onToggle={toggle} /></Col>
                    <Col xs={12}><button type="submit" className="sr-btn sr-btn-primary w-100" disabled={saving}>{saving ? <Spinner animation="border" size="sm" /> : <><AiOutlinePlus /> {t("addRepresentative")}</>}</button></Col>
                  </Row>
                </Form>
              )}

              {reps.length === 0 ? (
                students.length > 0 && <div className="sr-empty"><div className="sr-empty-ic">🧑‍🤝‍🧑</div><div className="sr-empty-t">{t("noRepresentatives")}</div><div className="sr-empty-h">{t("representativesEmptyHint")}</div></div>
              ) : (
                <>
                  <div className="sr-tablewrap">
                    <Table className="sr-table mb-0">
                      <thead><tr>
                        <th style={{ width: 34 }}>#</th><th>{t("representativeName")}</th>
                        <th>{t("representativeEmail")}</th><th className="text-center">{t("students")}</th><th className="text-end">{t("actionsCol")}</th>
                      </tr></thead>
                      <tbody>
                        {repsPage.map((r, i) => (
                          <tr key={r.uid}>
                            <td className="sr-idx">{(p - 1) * PER_PAGE + i + 1}</td>
                            <td>
                              <div className="sr-person"><Monogram name={r.displayName || r.email} />
                                <div><div className="sr-person-name">{r.displayName || "—"}</div></div>
                              </div>
                            </td>
                            <td><span className="sr-email" title={r.email}>{r.email}</span></td>
                            <td className="text-center"><span className="sr-badge">{(r.students || []).length}</span></td>
                            <td className="text-end text-nowrap">
                              <button type="button" className="sr-ic" title={t("viewDetails")} onClick={() => setDetail(r)}><AiOutlineEye /></button>
                              <button type="button" className="sr-ic" title={t("edit")} onClick={() => openEdit(r)}><AiOutlineEdit /></button>
                              <button type="button" className="sr-ic" title={t("resetPassword")} onClick={() => resetRep(r)}><MdLockReset /></button>
                              <button type="button" className="sr-ic sr-ic-danger" title={t("delete")} onClick={() => deleteRep(r)}><AiOutlineDelete /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Pager t={t} page={p} total={reps.length} perPage={PER_PAGE} onPage={setPage} />
                </>
              )}
            </div>
          )}
        </Container>
        <Footer logged={true} />
      </main>

      {/* Modal: detalle del representante */}
      <Modal show={!!detail} onHide={() => setDetail(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <span className="sr-sec-ic"><IoIosPeople /></span>{t("representativeDetail")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <>
              <div className="sr-detail-hero">
                <Monogram name={detail.displayName || detail.email} />
                <div><div className="sr-detail-name">{detail.displayName || "—"}</div><div className="sr-sub">{detail.email}</div></div>
              </div>
              <div className="sr-detail-head mt-3">{t("assignedStudents")} <span className="sr-count">{(detail.students || []).length}</span></div>
              {(detail.students || []).length === 0 ? <div className="sr-hint">{t("noStudentsAssigned")}</div> : (detail.students || []).map((s) => (
                <div key={s.uid} className="sr-detail-row">
                  <Monogram name={s.learnerName} />
                  <div><div className="sr-person-name">{s.learnerName || "—"}</div><div className="sr-sub">{s.email}</div></div>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal: editar representante */}
      <Modal show={!!editRep} onHide={() => setEditRep(null)} centered scrollable>
        <Modal.Header closeButton><Modal.Title className="fs-5">{t("editRepresentative")}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveEdit}>
            <Row className="g-2">
              <Col xs={12}><Form.Control className="sr-input" placeholder={t("representativeName")} value={eName} onChange={(e) => setEName(e.target.value)} /></Col>
              <Col xs={12}><div className="sr-kv"><span className="sr-kv-k">{t("representativeEmail")}</span><span className="sr-kv-v">{editRep?.email}</span></div></Col>
              <Col xs={12}><StudentPicker t={t} students={students} selected={eSel} onToggle={toggleE} /></Col>
              <Col xs={12}><button type="submit" className="sr-btn sr-btn-primary w-100" disabled={savingE}>{savingE ? <Spinner animation="border" size="sm" /> : t("save")}</button></Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      <style>{`
        :root { --sr-plum:#632d43; --sr-pink:#eb2178; --sr-ink:#241f2b; --sr-muted:#8b8593; --sr-line:#eceaf0; }
        .sr-wrap { max-width: 980px; }
        .sr-head { display:flex; align-items:center; gap:14px; }
        .sr-head-ic { width:46px; height:46px; border-radius:14px; display:flex; align-items:center; justify-content:center;
          font-size:1.5rem; color:#fff; background:linear-gradient(135deg,var(--sr-plum),var(--sr-pink)); flex-shrink:0; box-shadow:0 8px 18px rgba(99,45,67,.25); }
        .sr-eyebrow { font-size:.72rem; text-transform:uppercase; letter-spacing:1.2px; color:var(--sr-muted); font-weight:700; }
        .sr-title { font-size:1.7rem; font-weight:800; color:var(--sr-ink); margin:0; letter-spacing:-.5px; }
        .sr-desc { color:var(--sr-muted); margin:10px 0 22px; font-size:.95rem; max-width:640px; }
        .sr-card { background:#fff; border:1px solid var(--sr-line); border-radius:18px; box-shadow:0 4px 24px rgba(36,31,43,.05); margin-bottom:14px; padding:18px 20px; }
        .sr-card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
        .sr-card-title { font-size:1.05rem; font-weight:800; color:var(--sr-ink); margin:0; }
        .sr-count { display:inline-block; min-width:22px; text-align:center; padding:1px 8px; border-radius:999px; background:#f0e9f7; color:#6a3ec9; font-size:.78rem; font-weight:800; margin-left:4px; }
        .sr-form { margin-bottom:18px; }
        .sr-input { border:1.5px solid var(--sr-line) !important; border-radius:11px !important; padding:10px 13px !important; font-size:.9rem !important; box-shadow:none !important; }
        .sr-input:focus { border-color:var(--sr-pink) !important; }
        .sr-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; border:none; border-radius:11px; padding:10px 18px; font-size:.9rem; font-weight:700; cursor:pointer; transition:all .15s; }
        .sr-btn-primary { background:linear-gradient(135deg,var(--sr-plum),var(--sr-pink)); color:#fff; box-shadow:0 6px 16px rgba(235,33,120,.22); }
        .sr-btn-primary:hover { filter:brightness(1.05); transform:translateY(-1px); }
        .sr-btn-soft { background:#f4eef1; color:var(--sr-plum); }
        .sr-btn-soft:hover { background:#ece3e8; }
        .sr-label { font-size:.8rem; font-weight:800; color:var(--sr-ink); margin:8px 0 8px; text-transform:uppercase; letter-spacing:.4px; }
        .sr-search { display:flex; align-items:center; gap:8px; border:1.5px solid var(--sr-line); border-radius:11px; padding:8px 12px; margin-bottom:10px; color:var(--sr-muted); }
        .sr-search input { border:none; outline:none; flex:1; font-size:.9rem; background:transparent; }
        .sr-picker { max-height:230px; overflow-y:auto; border:1px solid var(--sr-line); border-radius:12px; padding:6px; }
        .sr-pick-row { display:flex; align-items:center; gap:10px; padding:7px 9px; border-radius:10px; cursor:pointer; margin:0; transition:background .12s; }
        .sr-pick-row:hover { background:#fcfafb; }
        .sr-pick-row.on { background:#f7f0fb; }
        .sr-pick-row input { width:17px; height:17px; accent-color:var(--sr-pink); flex-shrink:0; }
        .sr-pick-info { display:flex; flex-direction:column; min-width:0; }
        .sr-mono-av { width:32px; height:32px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; color:#fff; font-weight:800; font-size:.74rem; flex-shrink:0; }
        .sr-person { display:flex; align-items:center; gap:11px; }
        .sr-person-name { font-weight:600; color:var(--sr-ink); }
        .sr-sub { color:var(--sr-muted); font-size:.78rem; margin-top:1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .sr-tablewrap { overflow-x:auto; }
        .sr-table { width:100%; min-width:520px; border-collapse:separate; border-spacing:0; margin:0; }
        .sr-table thead th { font-size:.68rem; text-transform:uppercase; letter-spacing:.6px; color:var(--sr-muted); font-weight:800; border-bottom:2px solid var(--sr-line); padding:10px; white-space:nowrap; }
        .sr-table tbody td { padding:11px 10px; border-bottom:1px solid #f4f2f6; vertical-align:middle; font-size:.9rem; color:var(--sr-ink); }
        .sr-table tbody tr:last-child td { border-bottom:none; }
        .sr-table tbody tr:hover td { background:#fcfafb; }
        .sr-idx { color:#c3bcc9; font-weight:700; font-size:.82rem; }
        .sr-email { color:var(--sr-muted); font-size:.84rem; }
        .sr-badge { display:inline-block; min-width:26px; padding:2px 9px; border-radius:999px; background:#e7f6f2; color:#2e7d6b; font-weight:800; font-size:.82rem; }
        .sr-ic { width:33px; height:33px; border-radius:9px; border:1px solid var(--sr-line); background:#fff; color:var(--sr-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:1rem; margin-left:5px; transition:all .15s; }
        .sr-ic:hover { background:#f7f0fb; border-color:#e2d3ec; color:var(--sr-pink); }
        .sr-ic-danger:hover { background:#fdeef2; border-color:#f5c6d5; color:#d61f5c; }
        .sr-pager { display:flex; align-items:center; justify-content:center; gap:16px; margin-top:14px; }
        .sr-page-btn { width:34px; height:34px; border-radius:10px; border:1.5px solid var(--sr-line); background:#fff; font-size:1.2rem; color:var(--sr-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; }
        .sr-page-btn:disabled { opacity:.35; cursor:default; }
        .sr-page-info { font-size:.84rem; color:var(--sr-muted); font-weight:700; }
        .sr-empty { text-align:center; padding:34px 20px; }
        .sr-empty-ic { font-size:2.2rem; margin-bottom:8px; }
        .sr-empty-t { font-weight:700; color:var(--sr-ink); }
        .sr-empty-h { color:var(--sr-muted); font-size:.85rem; margin-top:4px; }
        .sr-hint { color:var(--sr-muted); font-size:.88rem; padding:8px 2px; }
        .sr-sec-ic { width:38px; height:38px; border-radius:11px; display:inline-flex; align-items:center; justify-content:center; font-size:1.15rem; background:#f0e9f7; color:#6a3ec9; flex-shrink:0; }
        .sr-detail-hero { display:flex; align-items:center; gap:13px; padding-bottom:6px; }
        .sr-detail-name { font-weight:800; font-size:1.1rem; color:var(--sr-ink); }
        .sr-detail-head { font-weight:800; font-size:.9rem; color:var(--sr-ink); margin:10px 0 8px; display:flex; align-items:center; gap:6px; }
        .sr-detail-row { display:flex; align-items:center; gap:11px; padding:8px 0; border-top:1px solid #f4f2f6; }
        .sr-kv { display:flex; justify-content:space-between; gap:10px; padding:8px 0; border-bottom:1px solid #f4f2f6; }
        .sr-kv-k { color:var(--sr-muted); font-size:.85rem; font-weight:600; }
        .sr-kv-v { color:var(--sr-ink); font-size:.88rem; font-weight:600; text-align:right; }
        @media (max-width:576px){ .sr-title { font-size:1.4rem; } .sr-card { padding:14px 13px; } }
      `}</style>
    </div>
  );
};

export default SchoolRepresentatives;
